// Roofing Common-Fix Protocols — Philly + Mid-Atlantic focused. Flat-roof
// heavy because 60-70% of rowhouse stock in the region is low-slope. Tapping
// a protocol auto-fills roof_type + system_product + notes on the new-job
// form. Zero LLM calls.
//
// Sources: NRCA manual, TPO/EPDM manufacturer tech bulletins, common Philly
// rowhouse punch-list patterns.

export const ROOFING_COMMON_FIXES = [
  // ── Flat / Low-Slope (Philly rowhouse dominant) ─────────
  {
    id: "tpo_seam_open",
    label: "TPO seam split / open",
    kind: "flat_tpo",
    signs: "Water intrusion tracking to a seam line, membrane peeling apart at hot-air weld",
    protocol: [
      "Clean 6 inches either side of the seam with TPO primer",
      "Re-weld with hot-air gun at 1000°F, 3-inch overlap",
      "Cover-strip failed section with new TPO cut-in and heat-weld both edges",
      "Water-test seam probe after cool-down",
    ],
    recommended_product: "GAF EverGuard TPO 60-mil",
    warranty_note: "Voids no active NDL if welded per manufacturer spec",
    urgency: "high",
  },
  {
    id: "tpo_puncture_repair",
    label: "TPO membrane puncture",
    kind: "flat_tpo",
    signs: "Small hole from HVAC tech / falling tool, membrane cover intact but pierced",
    protocol: [
      "Cut a 6-inch square TPO patch of matching thickness",
      "Prime both surfaces with TPO cleaner",
      "Heat-weld patch centered over puncture · roll seal",
      "Photo before/after for warranty file",
    ],
    recommended_product: "GAF EverGuard TPO 60-mil",
    urgency: "medium",
  },
  {
    id: "tpo_ponding",
    label: "TPO ponding water / low spot",
    kind: "flat_tpo",
    signs: "Water pooled >48hrs after rain, membrane starting to chalk / crack from UV",
    protocol: [
      "Assess whether it's a structural low or clogged drain — drain first",
      "Silicone coating restoration: GAF Unisil at 1.5 gal/100 sf",
      "For chronic ponding: add tapered ISO board + new TPO cap",
      "Warn homeowner: ponding voids most 20-yr NDL warranties",
    ],
    recommended_product: "GAF Unisil High-Solids Silicone",
    warranty_note: "Silicone is code-approved for ponded water",
    urgency: "medium",
  },
  {
    id: "epdm_seam_pull",
    label: "EPDM lap seam pulling apart",
    kind: "flat_epdm",
    signs: "Old rubber (Philly rowhouses 2000s era), splice tape releasing, leak at seam",
    protocol: [
      "Cut back failed splice 6 inches into good adhesion",
      "Clean with EPDM primer (Weathered Membrane Cleaner)",
      "Apply 6-inch cover strip with in-seam splice tape + lap sealant edge",
      "Roll firmly with steel roller",
    ],
    recommended_product: "Firestone RubberGard EPDM 60-mil",
    urgency: "high",
  },
  {
    id: "epdm_shrinkage",
    label: "EPDM shrinkage pulling from parapet",
    kind: "flat_epdm",
    signs: "Membrane visibly pulling away from wall termination, T-bar bent, screws exposed",
    protocol: [
      "Recover option: skin over existing with 60-mil EPDM + new termination bar",
      "Full re-cover if shrinkage >2 inches or field membrane brittle",
      "Install new counterflashing with sealant and mechanical fastener",
    ],
    recommended_product: "Firestone RubberGard EPDM 60-mil",
    warranty_note: "Most shrinkage is post-warranty (15+ yr old EPDM)",
    urgency: "medium",
  },
  {
    id: "mod_bit_blister",
    label: "Modified bitumen · blistering",
    kind: "flat_modified_bitumen",
    signs: "Raised bubbles in cap sheet, especially south-facing exposure",
    protocol: [
      "Score blister with utility knife in an X pattern",
      "Press flat, apply flashing cement inside cavity",
      "Torch-seal (or cold-adhesive) a modified bitumen patch over top",
      "Do NOT torch on Philly rowhouses adjacent to combustibles — cold-app patch instead",
    ],
    recommended_product: "GAF Ruberoid Torch Granule",
    urgency: "low",
  },
  {
    id: "mod_bit_alligator",
    label: "Modified bitumen · alligatoring / UV weathering",
    kind: "flat_modified_bitumen",
    signs: "Cracked/checkered surface pattern, granular loss, no active leak yet",
    protocol: [
      "Prep: sweep loose granules, prime with asphalt emulsion",
      "Apply reflective coating: Henry Tropi-Cool 887 Silicone at 1.5 gal/100 sf",
      "Reset the maintenance clock 10 years",
      "Alternative: torch new cap sheet if substrate solid",
    ],
    recommended_product: "Henry Tropi-Cool 887 Silicone",
    warranty_note: "10-yr coating warranty from GAF/Henry when done to spec",
    urgency: "low",
  },
  {
    id: "flat_drain_clog",
    label: "Roof drain / scupper clogged",
    kind: "flat_tpo",
    signs: "Ponding around drain body, debris packed in strainer, water backing up",
    protocol: [
      "Physically clear debris + snake the drain line",
      "Inspect the drain body seal / clamping ring — replace if compromised",
      "Verify no ice/root intrusion below roof deck",
      "Schedule biannual drain cleaning as recurring maintenance",
    ],
    recommended_product: "n/a — service only",
    urgency: "high",
  },
  {
    id: "flat_parapet_flashing",
    label: "Parapet wall flashing failure",
    kind: "flat_tpo",
    signs: "Water staining on interior ceiling adjacent to parapet, coping cap loose",
    protocol: [
      "Remove old counterflashing + termination bar",
      "New TPO flashing wrapped over parapet top",
      "Aluminum coping cap with proper cleats + sealed joints",
      "Watch for through-wall flashing failure below — often the real culprit on old brick",
    ],
    recommended_product: "GAF EverGuard TPO 60-mil",
    urgency: "high",
  },

  // ── Pitched roofs (South Jersey / Delco twins) ──────────
  {
    id: "shingle_wind_uplift",
    label: "Asphalt shingle wind damage",
    kind: "asphalt_architectural",
    signs: "Missing tabs, exposed nail heads, creased shingles from lifting event",
    protocol: [
      "Match manufacturer + color — GAF Timberline HDZ is most common in region",
      "Replace damaged shingles + 2 courses up (nail through cutouts)",
      "Seal each hand-nailed tab with 1-inch dot of asphalt cement",
      "Insurance photo: wide shot + close-up of every damage point",
    ],
    recommended_product: "GAF Timberline HDZ",
    urgency: "high",
  },
  {
    id: "shingle_granular_loss",
    label: "Asphalt shingle · heavy granular loss",
    kind: "asphalt_architectural",
    signs: "Bald spots visible from ground, granules in gutters, shingles 15+ yrs old",
    protocol: [
      "Assess: >30% granular loss = end of service life, quote replacement",
      "Full tear-off recommended (2nd layer over-installs void most Lifetime warranties)",
      "Upgrade to impact-rated shingle if hail-prone area (Malarkey Vista Class 4)",
    ],
    recommended_product: "GAF Timberline HDZ",
    urgency: "medium",
  },
  {
    id: "ice_dam_gutter",
    label: "Ice dam · gutter overflow (winter)",
    kind: "asphalt_architectural",
    signs: "Ice buildup at eave, water backup into soffit / ceiling in winter",
    protocol: [
      "Immediate: steam removal of ice dam (do NOT chip — damages shingles)",
      "Root cause: attic insulation R-38 minimum + baffled soffit ventilation",
      "Long-term: install Grace Ice & Water Shield 3 ft up from eave when re-roofing",
      "Heat-tape as last resort (electricity bill $$$)",
    ],
    recommended_product: "Grace Ice & Water Shield",
    urgency: "high",
    warranty_note: "Ice & Water Shield 3-ft up from eave is PA code minimum",
  },
];

export function protocolsForRoofKind(roof_type, limit = 6) {
  if (!roof_type) return ROOFING_COMMON_FIXES.slice(0, limit);
  const key = String(roof_type).toLowerCase();
  // Loose kind match — 'flat_tpo' → tpo, 'asphalt_architectural' → asphalt, etc.
  const KIND_HINTS = {
    tpo: "flat_tpo",
    epdm: "flat_epdm",
    "modified": "flat_modified_bitumen", "bitumen": "flat_modified_bitumen", "mod_bit": "flat_modified_bitumen",
    asphalt: "asphalt_architectural", shingle: "asphalt_architectural",
    metal: "metal_standing_seam",
  };
  let match = null;
  for (const [hint, cat] of Object.entries(KIND_HINTS)) {
    if (key.includes(hint)) { match = cat; break; }
  }
  const filtered = match
    ? ROOFING_COMMON_FIXES.filter((f) => f.kind === match)
    : ROOFING_COMMON_FIXES;
  return (filtered.length ? filtered : ROOFING_COMMON_FIXES).slice(0, limit);
}
